import { api, apiText } from './client'
import type {
  AssignedProfile,
  UserLimits,
  JobDeleteInput,
  JobDeletePreview,
  JobDeleteResult,
  FairPlayCredential,
  FairPlayCredentialPatch,
  KeyPool,
  ManifestKind,
  KeyPoolCreate,
  KeyPoolKeyPage,
  KeyPoolKeySort,
  KeyPoolKeyStatus,
  KeyPoolPatch,
  KeyProvider,
  KeyProviderInput,
  KeyProviderTest,
  SigmaJWTCheckRequest,
  SigmaJWTCheckResult,
  KeyProviderUsage,
  KeyToken,
  TokenKey,
  TokenRevocation,
  ApiClient,
  ApiClientKey,
  CreatedApiClientKey,
  LegacyRequest,
  LegacySummary,
  AuditLog,
  BulkResult,
  DashboardOverview,
  ImageSpec,
  Job,
  JobPurpose,
  JobAttempt,
  JobEvent,
  JobOutput,
  JobStats,
  LoginResponse,
  Backend,
  NodeInfo,
  NodeMetricSample,
  NodeMount,
  NodeProvisioned,
  CodecCatalog,
  HTTPStorageConfig,
  LocalStorageConfig,
  StorageKind,
  StorageTestResult,
  OutputFormat,
  PreviewCommand,
  Profile,
  ProfileUsage,
  ProfileSpec,
  ProfileVersion,
  TestEncode,
  Page,
  Queue,
  QueueAssignment,
  QueueAssignmentInput,
  QueueDetail,
  QueueDrainResult,
  QueueJobs,
  QueueRequirements,
  QueueWaitBucket,
  ServiceQuota,
  NodeQueues,
  RoutingRule,
  RoutingRuleInput,
  RoutingTestInput,
  MoveResult,
  AlertItem,
  AlertSummary,
  Role,
  Setting,
  Storage,
  User,
  WebhookDelivery,
  UsageOverview,
  UsagePoint,
  StorageUsage,
  BrowseResult,
  ReconcileRun,
  RetentionPolicy,
  RetentionPolicyInput,
  DeletionRun,
  DeletionItem,
  SnapshotBody,
  ObjectPage,
  JobStatistics,
  StatsQuery,
  StatsReport,
  ReqInstance,
  RequestBreakdownBy,
  RequestBreakdown,
  RequestEventPage,
  RequestGranularity,
  RequestSeries,
  RequestStatsMeta,
  RequestAction,
  RequestRouteSort,
  RequestRoutesResponse,
  SigmaSummary,
} from './types'

export const authApi = {
  login: (email: string, password: string) =>
    api<LoginResponse>('/auth/login', { method: 'POST', body: { email, password }, anonymous: true }),
  me: () => api<User>('/auth/me'),
  logoutAll: () => api<void>('/auth/logout-all', { method: 'POST' }),
}

export interface UserCreate {
  email: string
  name: string
  password: string
  role: Role
}

export interface UserPatch {
  name?: string
  role?: Role
  disabled?: boolean
  password?: string
}

export const usersApi = {
  list: (params: { page: number; page_size: number; q?: string }) => api<Page<User>>('/users', { query: params }),
  create: (body: UserCreate) => api<User>('/users', { method: 'POST', body }),
  update: (id: number, body: UserPatch) => api<User>(`/users/${id}`, { method: 'PATCH', body }),
  profiles: (id: number) => api<{ items: AssignedProfile[] }>(`/users/${id}/profiles`),
  setProfiles: (id: number, profileIds: number[]) =>
    api<{ items: AssignedProfile[] }>(`/users/${id}/profiles`, { method: 'PUT', body: { profile_ids: profileIds } }),
  limits: (id: number) => api<UserLimits>(`/users/${id}/limits`),
  setLimits: (id: number, body: UserLimits) => api<UserLimits>(`/users/${id}/limits`, { method: 'PUT', body }),
}

export interface ApiClientWatermark {
  logo_url?: string
  position?: string
  scale?: number
  margin?: number
}

export interface ApiClientInput {
  service_code?: string
  name?: string
  description?: string
  set_legacy_credentials?: boolean
  legacy_username?: string | null
  legacy_password?: string | null
  ip_allowlist?: string[]
  default_queue?: string
  allowed_queues?: string[]
  watermark?: ApiClientWatermark
  allow_retention_on_client_buckets?: boolean
  enabled?: boolean
  default_profile_id?: number
  clear_default_profile?: boolean
}

export const apiClientsApi = {
  list: (params: { page: number; page_size: number }) => api<Page<ApiClient>>('/api-clients', { query: params }),
  create: (body: ApiClientInput) => api<ApiClient>('/api-clients', { method: 'POST', body }),
  update: (id: number, body: ApiClientInput) => api<ApiClient>(`/api-clients/${id}`, { method: 'PATCH', body }),
  keys: (id: number) => api<{ items: ApiClientKey[] }>(`/api-clients/${id}/keys`),
  createKey: (id: number, name: string) => api<CreatedApiClientKey>(`/api-clients/${id}/keys`, { method: 'POST', body: { name } }),
  revokeKey: (id: number, keyId: number) => api<ApiClientKey>(`/api-clients/${id}/keys/${keyId}/revoke`, { method: 'POST' }),
}

export interface LegacyRequestParams {
  page: number
  page_size: number
  endpoint?: string
  service?: string
  result_code?: number
  job_id?: number
  remote_ip?: string
  unknown?: '1'
  notes?: '1'
}

export const legacyRequestsApi = {
  list: (params: LegacyRequestParams) => api<Page<LegacyRequest>>('/legacy-requests', { query: { ...params } }),
  get: (id: number) => api<LegacyRequest>(`/legacy-requests/${id}`),
  summary: (days: number) => api<LegacySummary>('/legacy-requests/summary', { query: { days } }),
}

export const settingsApi = {
  list: (prefix?: string) => api<{ items: Setting[] }>('/settings', { query: { prefix } }),
  update: (key: string, value: unknown, version: number) =>
    api<Setting>(`/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: { value, version } }),
}

export const auditApi = {
  list: (params: { page: number; page_size: number; resource_type?: string; action?: string }) =>
    api<Page<AuditLog>>('/audit-logs', { query: params }),
}

export interface JobListParams {
  page: number
  page_size: number
  status?: string
  queue?: string
  service?: string
  q?: string
  purpose?: JobPurpose
  profile?: string
  /** `only` = chỉ job đã xoá mềm. */
  deleted?: 'only'
  /** viewer+: lọc theo user tạo job (transcoder bị ép = mình). */
  created_by?: number
}
export type JobDeletePreviewParams = Omit<JobListParams, 'page' | 'page_size'>

export interface JobCreate {
  /** URL source (legacy field); omitted for a storage source. */
  source_url?: string
  source?:
    | { type: 'url'; url: string }
    | { type: 'storage'; storage_id: number; key: string }
    | { type: 'manifest'; url: string; manifest: ManifestKind }
  service?: string
  queue?: string
  priority?: number
  dest_path?: string
  callback_url?: string
  storage_id?: number
  trim?: { start?: number; end?: number }
  /** Custom output (no profile). */
  output?: {
    format: OutputFormat
    renditions?: { height: number; video_bitrate_k?: number }[]
    thumbnails?: number
    images?: ImageSpec
    preset?: string
    segment_seconds?: number
  }
  profile_id?: number
  profile_version?: number
  /** Admin tạo thay user transcoder (priority được phép). */
  on_behalf_of?: number
}

export interface RetryBody {
  queue?: string
  priority?: number
  profile_id?: number
  profile_version?: number
}

type Items<T> = { items: T[] }

export const jobsApi = {
  list: (params: JobListParams) => api<Page<Job>>('/jobs', { query: { ...params } }),
  deletePreview: (params: JobDeletePreviewParams) => api<JobDeletePreview>('/jobs/delete-preview', { query: { ...params } }),
  stats: () => api<JobStats>('/jobs/stats'),
  get: (id: number) => api<Job>(`/jobs/${id}`),
  attempts: (id: number) => api<Items<JobAttempt>>(`/jobs/${id}/attempts`),
  events: (id: number) => api<Items<JobEvent>>(`/jobs/${id}/events`),
  outputs: (id: number) => api<Items<JobOutput>>(`/jobs/${id}/outputs`),
  webhooks: (id: number) => api<Items<WebhookDelivery>>(`/jobs/${id}/webhooks`),
  create: (body: JobCreate) => api<Job>('/jobs', { method: 'POST', body }),
  snapshot: (body: SnapshotBody) => api<Job>('/snapshots', { method: 'POST', body }),
  cancel: (id: number, reason?: string) => api<Job>(`/jobs/${id}/cancel`, { method: 'POST', body: { reason } }),
  retry: (id: number, body: RetryBody = {}) => api<Job>(`/jobs/${id}/retry`, { method: 'POST', body }),
  setPriority: (id: number, body: { priority?: number; to_front?: boolean; reason?: string }) =>
    api<Job>(`/jobs/${id}/priority`, { method: 'POST', body }),
  bulkRetry: (ids: number[]) => api<BulkResult>('/jobs/bulk-retry', { method: 'POST', body: { ids } }),
  bulkCancel: (ids: number[], reason?: string) => api<BulkResult>('/jobs/bulk-cancel', { method: 'POST', body: { ids, reason } }),
  remove: (id: number, o: { deleteOutputs: boolean; deleteKeys?: boolean; reason?: string; includeClientBuckets?: boolean }) =>
    api<JobDeleteResult>(`/jobs/${id}`, {
      method: 'DELETE',
      query: {
        delete_outputs: o.deleteOutputs ? 'true' : undefined,
        delete_keys: o.deleteKeys ? 'true' : undefined,
        reason: o.reason || undefined,
        include_client_buckets: o.includeClientBuckets ? 'true' : undefined,
      },
    }),
  bulkDelete: (body: JobDeleteInput) => api<JobDeleteResult>('/jobs/bulk-delete', { method: 'POST', body }),
  move: (body: JobMoveBody) => api<MoveResult>('/jobs/move', { method: 'POST', body }),
  moveOne: (id: number, body: JobMoveOptions) => api<MoveResult>(`/jobs/${id}/move`, { method: 'POST', body }),
  requeueRunning: (id: number, body: RequeueRunningBody) =>
    api<Job>(`/jobs/${id}/requeue-running`, { method: 'POST', body: { ...body, confirm: true } }),
  setRetention: (id: number, body: JobRetentionBody) => api<Job>(`/jobs/${id}/retention`, { method: 'PUT', body }),
}

export interface JobRetentionBody {
  legal_hold: boolean
  reason?: string
  retention_until?: string | null
}

/** Options shared by bulk and single-job moves (only QUEUED jobs move). */
export interface JobMoveOptions {
  /** Omitted = priority-only change. */
  target_queue?: string
  priority?: number
  to_front?: boolean
  keep_enqueue_time?: boolean
  dry_run?: boolean
  reason?: string
}

export interface JobMoveFilter {
  queue?: string
  service?: string
  profile?: string
  created_from?: string
  created_to?: string
}

export interface JobMoveBody extends JobMoveOptions {
  job_ids?: number[]
  filter?: JobMoveFilter
}

export interface RequeueRunningBody {
  target_queue?: string
  priority?: number
  to_front?: boolean
  keep_enqueue_time?: boolean
  reason?: string
}

export interface QueueInput {
  code?: string
  name: string
  description?: string
  rank?: number
  status?: 'active' | 'paused'
  max_running?: number
  max_queued?: number
  overflow_queue?: string
  service_quotas?: Record<string, ServiceQuota>
  requirements?: QueueRequirements
  aging_interval_sec?: number
  aging_step?: number
  default_priority?: number
  sla_wait_seconds?: number
  assign_all_nodes?: boolean
}

export const queuesApi = {
  list: () => api<Items<Queue>>('/queues'),
  get: (id: number) => api<QueueDetail>(`/queues/${id}`),
  create: (body: QueueInput) => api<Queue>('/queues', { method: 'POST', body }),
  update: (id: number, body: QueueInput) => api<Queue>(`/queues/${id}`, { method: 'PATCH', body }),
  remove: (id: number, targetQueue?: string) =>
    api<{ mode: 'hard' | 'soft'; drain?: QueueDrainResult }>(`/queues/${id}`, { method: 'DELETE', query: { target_queue: targetQueue } }),
  pause: (id: number) => api<Queue>(`/queues/${id}/pause`, { method: 'POST' }),
  resume: (id: number) => api<Queue>(`/queues/${id}/resume`, { method: 'POST' }),
  drain: (id: number, body: { target_queue: string; archive: boolean; reason?: string }) =>
    api<QueueDrainResult>(`/queues/${id}/drain`, { method: 'POST', body }),
  purge: (id: number, body: { confirm: string; reason?: string }) => api<QueueDrainResult>(`/queues/${id}/purge`, { method: 'POST', body }),
  makeDefault: (id: number) => api<Queue>(`/queues/${id}/default`, { method: 'POST' }),
  jobs: (id: number, params: { page: number; page_size: number }) => api<QueueJobs>(`/queues/${id}/jobs`, { query: params }),
  stats: (id: number, hours: number) => api<{ items: QueueWaitBucket[]; bucket_sec: number }>(`/queues/${id}/stats`, { query: { hours } }),
  assignments: (id: number) => api<Items<QueueAssignment>>(`/queues/${id}/assignments`),
  setAssignments: (id: number, items: QueueAssignmentInput[]) =>
    api<Items<QueueAssignment>>(`/queues/${id}/assignments`, { method: 'PUT', body: { items } }),
}

export const assignmentsApi = {
  list: () => api<Items<QueueAssignment>>('/queue-assignments'),
  create: (body: QueueAssignmentInput) => api<QueueAssignment>('/queue-assignments', { method: 'POST', body }),
  update: (id: number, body: Pick<QueueAssignmentInput, 'position' | 'reserved_slots' | 'gpu_indices' | 'enabled'>) =>
    api<QueueAssignment>(`/queue-assignments/${id}`, { method: 'PATCH', body }),
  remove: (id: number) => api<void>(`/queue-assignments/${id}`, { method: 'DELETE' }),
}

export const routingRulesApi = {
  list: () => api<Items<RoutingRule>>('/queue-routing-rules'),
  create: (body: RoutingRuleInput) => api<RoutingRule>('/queue-routing-rules', { method: 'POST', body }),
  update: (id: number, body: RoutingRuleInput) => api<RoutingRule>(`/queue-routing-rules/${id}`, { method: 'PATCH', body }),
  remove: (id: number) => api<void>(`/queue-routing-rules/${id}`, { method: 'DELETE' }),
  reorder: (ids: number[]) => api<Items<RoutingRule>>('/queue-routing-rules/order', { method: 'POST', body: { ids } }),
  test: (body: RoutingTestInput) => api<{ rule: RoutingRule | null; queue: string }>('/queue-routing-rules/test', { method: 'POST', body }),
}

export interface AlertListParams {
  page: number
  page_size: number
  state?: 'open' | 'all'
  severity?: string
  kind?: string
}

export const alertsApi = {
  list: (params: AlertListParams) => api<Page<AlertItem>>('/alerts', { query: { ...params } }),
  summary: () => api<AlertSummary>('/alerts/summary'),
  ack: (id: number) => api<AlertItem>(`/alerts/${id}/ack`, { method: 'POST' }),
}

export interface NodeCreate {
  name: string
  labels?: string[]
  concurrency?: number
  note?: string
}

export interface NodePatch {
  labels?: string[]
  concurrency?: number
  clear_concurrency?: boolean
  maintenance?: boolean
  note?: string
}

export const nodesApi = {
  list: () => api<Items<NodeInfo>>('/nodes'),
  listWithRemoved: () => api<Items<NodeInfo>>('/nodes', { query: { include_removed: 'true' } }),
  get: (id: number) => api<NodeInfo>(`/nodes/${id}`),
  metrics: (id: number, minutes: number) => api<Items<NodeMetricSample>>(`/nodes/${id}/metrics`, { query: { minutes } }),
  create: (body: NodeCreate) => api<NodeProvisioned>('/nodes', { method: 'POST', body }),
  joinToken: (id: number) => api<NodeProvisioned>(`/nodes/${id}/join-token`, { method: 'POST' }),
  update: (id: number, body: NodePatch) => api<NodeInfo>(`/nodes/${id}`, { method: 'PATCH', body }),
  drain: (id: number) => api<NodeInfo>(`/nodes/${id}/drain`, { method: 'POST' }),
  enable: (id: number) => api<NodeInfo>(`/nodes/${id}/enable`, { method: 'POST' }),
  disable: (id: number) => api<NodeInfo>(`/nodes/${id}/disable`, { method: 'POST' }),
  remove: (id: number, force = false) => api<NodeInfo>(`/nodes/${id}`, { method: 'DELETE', query: force ? { force: 'true' } : {} }),
  setGpuSlots: (id: number, slots: Record<string, number>) =>
    api<NodeInfo>(`/nodes/${id}/gpu-slots`, { method: 'PUT', body: { slots } }),
  queues: (id: number) => api<NodeQueues>(`/nodes/${id}/queues`),
  setQueues: (id: number, items: NodeQueueInput[]) => api<NodeQueues>(`/nodes/${id}/queues`, { method: 'PUT', body: { items } }),
  /** Issues a new agent credential (delivered on next heartbeat); 409 when the node has none. */
  rotateCredential: (id: number) => api<NodeInfo>(`/nodes/${id}/rotate-credential`, { method: 'POST' }),
}

export const dashboardApi = {
  get: (hours: number) => api<DashboardOverview>('/dashboard', { query: { hours } }),
}

export interface NodeQueueInput {
  queue: string
  position: number
  reserved_slots: number
  gpu_indices: number[] | null
  enabled: boolean
}

export type OpenAPISpec = Record<string, unknown> & { paths?: Record<string, Record<string, unknown>> }

export const docsApi = {
  openapi: () => api<OpenAPISpec>('/openapi.json'),
}

export const codecsApi = {
  catalog: () => api<CodecCatalog>('/codecs'),
}

export interface ProfileCreate {
  code: string
  name: string
  description?: string
  spec: ProfileSpec
  note?: string
  is_default?: boolean
  license_ack?: boolean
}

export interface ProfileUpdate {
  name?: string
  description?: string
  spec?: ProfileSpec
  note?: string
  base_version?: number
  license_ack?: boolean
}

export interface SampleSource {
  width: number
  height: number
  fps?: number
  codec?: string
  hdr?: '' | 'pq' | 'hlg'
  rotation?: number
  audio?: boolean
}

export interface PreviewBody {
  profile_id?: number
  version?: number
  spec?: ProfileSpec
  backends?: Backend[]
  source: SampleSource
  watermark?: boolean
}

export interface TestEncodeBody {
  source_url?: string
  version?: number
  backend?: Backend
  vmaf?: boolean
  trim?: { start?: number; end?: number }
}

/** POST /profiles/{id}/sample: encode a short clip (samples.max_seconds). */
export interface SampleBody {
  source_url?: string
  version?: number
  start?: number
  duration?: number
  vmaf?: boolean
}

export const profilesApi = {
  list: (includeArchived = false) =>
    api<Items<Profile>>('/profiles', { query: includeArchived ? { include_archived: 'true' } : {} }),
  get: (id: number) => api<Profile>(`/profiles/${id}`),
  versions: (id: number) => api<Items<ProfileVersion>>(`/profiles/${id}/versions`),
  create: (body: ProfileCreate) => api<Profile>('/profiles', { method: 'POST', body }),
  update: (id: number, body: ProfileUpdate) => api<Profile>(`/profiles/${id}`, { method: 'PATCH', body }),
  archive: (id: number) => api<Profile>(`/profiles/${id}/archive`, { method: 'POST' }),
  restore: (id: number) => api<Profile>(`/profiles/${id}/restore`, { method: 'POST' }),
  makeDefault: (id: number) => api<Profile>(`/profiles/${id}/default`, { method: 'POST' }),
  usage: (id: number) => api<ProfileUsage>(`/profiles/${id}/usage`),
  remove: (id: number) => api<void>(`/profiles/${id}`, { method: 'DELETE' }),
  preview: (body: PreviewBody) =>
    api<{ commands: PreviewCommand[] }>('/profiles/preview-command', { method: 'POST', body }),
  testEncode: (id: number, body: TestEncodeBody) => api<Job>(`/profiles/${id}/test-encode`, { method: 'POST', body }),
  testEncodes: (id: number) => api<Items<TestEncode>>(`/profiles/${id}/test-encodes`),
  sample: (id: number, body: SampleBody) => api<Job>(`/profiles/${id}/sample`, { method: 'POST', body }),
}

export interface StorageInput {
  code?: string
  name?: string
  kind?: StorageKind
  http?: HTTPStorageConfig
  local?: LocalStorageConfig
  clear_secret?: boolean
  endpoint?: string
  region?: string
  bucket?: string
  prefix?: string
  access_key?: string
  secret_key?: string
  use_ssl?: boolean
  path_style?: boolean
  public_base_url?: string
  is_default?: boolean
  /** Admin tạo hộ storage của user transcoder. */
  owner_user_id?: number | null
}

export interface StorageListParams {
  owner_user_id?: number
  scope?: 'shared' | 'owned' | 'all'
}

export const storagesApi = {
  // list không nhận tham số: nhiều nơi truyền thẳng làm queryFn (nhận QueryFunctionContext).
  list: () => api<Items<Storage>>('/storages'),
  listBy: (params: StorageListParams) => api<Items<Storage>>('/storages', { query: { ...params } }),
  remove: (id: number) => api<void>(`/storages/${id}`, { method: 'DELETE' }),
  create: (body: StorageInput) => api<Storage>('/storages', { method: 'POST', body }),
  update: (id: number, body: StorageInput) => api<Storage>(`/storages/${id}`, { method: 'PATCH', body }),
  test: (id: number) => api<StorageTestResult>(`/storages/${id}/test`, { method: 'POST' }),
  nodes: (id: number) => api<Items<NodeMount>>(`/storages/${id}/nodes`),
  /** Objects of an s3 / local storage (job source picker), key order, cursor = last key. */
  objects: (id: number, prefix: string, cursor?: string, limit = 100) =>
    api<ObjectPage>(`/storages/${id}/objects`, { query: { prefix, cursor: cursor ?? '', limit: String(limit) } }),
}

export const drmApi = {
  providers: () => api<Items<KeyProvider>>('/drm/key-providers'),
  createProvider: (body: KeyProviderInput) => api<KeyProvider>('/drm/key-providers', { method: 'POST', body }),
  updateProvider: (id: number, body: KeyProviderInput) => api<KeyProvider>(`/drm/key-providers/${id}`, { method: 'PATCH', body }),
  providerUsage: (id: number) => api<KeyProviderUsage>(`/drm/key-providers/${id}/usage`),
  deleteProvider: (id: number, force = false) => api<void>(`/drm/key-providers/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  testProvider: (id: number) => api<KeyProviderTest>(`/drm/key-providers/${id}/test`, { method: 'POST' }),
  jwtCheck: (id: number, body: SigmaJWTCheckRequest) =>
    api<SigmaJWTCheckResult>(`/drm/key-providers/${id}/jwt-check`, { method: 'POST', body }),
  keyToken: (jobId: number, ttlSeconds?: number) =>
    api<KeyToken>('/drm/key-tokens', { method: 'POST', body: { job_id: jobId, ttl_seconds: ttlSeconds ?? 0 } }),
  tokenKeys: () => api<Items<TokenKey>>('/drm/token-keys'),
  rotateTokenKey: () => api<TokenKey>('/drm/token-keys/rotate', { method: 'POST' }),
  disableTokenKey: (kid: string) => api<TokenKey>(`/drm/token-keys/${encodeURIComponent(kid)}/disable`, { method: 'POST' }),
  fairplayCredentials: () => api<Items<FairPlayCredential>>('/drm/fairplay-credentials'),
  /** multipart: certificate, private_key, password?, ask, name, primary, development. */
  createFairplayCredential: (form: FormData) => api<FairPlayCredential>('/drm/fairplay-credentials', { method: 'POST', formData: form }),
  updateFairplayCredential: (id: number, body: FairPlayCredentialPatch) =>
    api<FairPlayCredential>(`/drm/fairplay-credentials/${id}`, { method: 'PATCH', body }),
  deleteFairplayCredential: (id: number) => api<void>(`/drm/fairplay-credentials/${id}`, { method: 'DELETE' }),
  keyPools: () => api<Items<KeyPool>>('/drm/key-pools'),
  keyPool: (id: number) => api<KeyPool>(`/drm/key-pools/${id}`),
  createKeyPool: (body: KeyPoolCreate) => api<KeyPool>('/drm/key-pools', { method: 'POST', body }),
  updateKeyPool: (id: number, body: KeyPoolPatch) => api<KeyPool>(`/drm/key-pools/${id}`, { method: 'PATCH', body }),
  deleteKeyPool: (id: number) => api<void>(`/drm/key-pools/${id}`, { method: 'DELETE' }),
  keyPoolKeys: (id: number, params: { status?: KeyPoolKeyStatus; sort: KeyPoolKeySort; page: number; page_size: number }) =>
    api<KeyPoolKeyPage>(`/drm/key-pools/${id}/keys`, {
      query: { ...(params.status ? { status: params.status } : {}), sort: params.sort, page: String(params.page), page_size: String(params.page_size) },
    }),
  /** Rotate the given KIDs, or every non-retired key (all). Jobs already packaged keep their old key. */
  rotateKeyPool: (id: number, body: { kids: string[] } | { all: true }) =>
    api<{ retired: string[] | null }>(`/drm/key-pools/${id}/rotate`, { method: 'POST', body }),
  /** Reject every key token of the job issued so far (ADR 0018 3.7). */
  revokeTokens: (jobId: number) => api<TokenRevocation>(`/jobs/${jobId}/drm/revoke-tokens`, { method: 'POST' }),
}

export interface QuotaInput {
  quota_bytes: number | null
  warn_percent: number
}

export const storageUsageApi = {
  overview: () => api<UsageOverview>('/storage-usage'),
  series: (params: { storage_id?: number; days: number }) => api<Items<UsagePoint>>('/storage-usage/series', { query: { ...params } }),
  setQuota: (id: number, body: QuotaInput) => api<StorageUsage>(`/storages/${id}/quota`, { method: 'PUT', body }),
  browse: (id: number, prefix: string) => api<BrowseResult>(`/storages/${id}/browse`, { query: { prefix } }),
  reconcile: (id: number, prefix: string) => api<ReconcileRun>(`/storages/${id}/reconcile`, { method: 'POST', body: { prefix } }),
  reconcileRuns: (id: number) => api<Items<ReconcileRun>>(`/storages/${id}/reconcile-runs`),
}

export const retentionApi = {
  list: () => api<Items<RetentionPolicy>>('/retention-policies'),
  get: (id: number) => api<RetentionPolicy>(`/retention-policies/${id}`),
  create: (body: RetentionPolicyInput) => api<RetentionPolicy>('/retention-policies', { method: 'POST', body }),
  update: (id: number, body: RetentionPolicyInput) => api<RetentionPolicy>(`/retention-policies/${id}`, { method: 'PUT', body }),
  remove: (id: number) => api<{ deleted: boolean }>(`/retention-policies/${id}`, { method: 'DELETE' }),
  dryRun: (id: number) => api<DeletionRun>(`/retention-policies/${id}/dry-run`, { method: 'POST' }),
}

export interface DeletionRunListParams {
  page: number
  page_size: number
  status?: string
  policy_id?: number
}

export interface ManualDeletionInput {
  job_ids: number[]
  reason: string
  scheduled_at?: string
  include_client_buckets?: boolean
  grace_hours?: number
}

export const deletionRunsApi = {
  list: (params: DeletionRunListParams) => api<Page<DeletionRun>>('/deletion-runs', { query: { ...params } }),
  get: (id: number) => api<DeletionRun>(`/deletion-runs/${id}`),
  items: (id: number, params: { status?: string; page: number; page_size: number }) =>
    api<Page<DeletionItem>>(`/deletion-runs/${id}/items`, { query: { ...params } }),
  createManual: (body: ManualDeletionInput) => api<DeletionRun>('/deletion-runs', { method: 'POST', body }),
  approve: (id: number) => api<DeletionRun>(`/deletion-runs/${id}/approve`, { method: 'POST' }),
  cancel: (id: number) => api<DeletionRun>(`/deletion-runs/${id}/cancel`, { method: 'POST' }),
}

const statsQuery = (q: StatsQuery) => ({
  from: q.from,
  to: q.to,
  group_by: q.group_by,
  tz: q.tz,
  profile_id: q.profile_id,
  api_client_id: q.api_client_id,
  user_id: q.user_id,
})

export const statsApi = {
  report: (q: StatsQuery) => api<StatsReport>('/stats', { query: statsQuery(q) }),
  csv: (q: StatsQuery) => apiText('/stats/export.csv', { query: statsQuery(q) }),
  job: (id: number) => api<JobStatistics>(`/jobs/${id}/stats`),
}

/**
 * Bộ lọc chung của thống kê request (plan request-stats + request-stats-redesign, "Hợp đồng API").
 * `op`/`result`/`area`/`flow` nhận danh sách phẩy; `exclude_preflight` = '1' để bỏ preflight khỏi tổng.
 */
export interface RequestFilter {
  from?: string
  to?: string
  kind?: string
  op?: string
  scheme?: string
  provider_id?: number
  result?: string
  status_class?: string
  instance?: string
  area?: string
  flow?: string
  action?: RequestAction
  exclude_preflight?: '1'
}

/** GET /request-stats/routes: sắp xếp top route theo raw; kind mặc định api,key nếu không truyền. */
export interface RequestRoutesQuery extends RequestFilter {
  sort?: RequestRouteSort
  limit?: number
}

export interface RequestSeriesQuery extends RequestFilter {
  granularity: RequestGranularity
  tz: string
}

export interface RequestBreakdownQuery extends RequestFilter {
  by: RequestBreakdownBy
}

/** Lọc raw theo id (chỉ bản ghi có id tương ứng). */
export interface RequestEventIdFilter {
  job_id?: number
  kid?: string
  status?: number
  req_id?: string
  upstream_req_id?: string
  request_id?: string
  merchant_id?: string
}

export interface RequestEventsQuery extends RequestFilter, RequestEventIdFilter {
  /** Khớp đúng pattern route (vd /api/v1/jobs/{id}). */
  route?: string
  cursor?: string
  limit?: number
}

export interface SigmaSummaryQuery {
  from?: string
  to?: string
  granularity: 'hour' | 'day'
  tz: string
  op?: string
  scheme?: string
  status?: number
  merchant_id?: string
  provider_id?: number
  /** Phẩy: op,endpoint,scheme,merchant,app,provider,status,result. */
  group_by?: string
}

export const requestStatsApi = {
  meta: () => api<RequestStatsMeta>('/request-stats/meta'),
  series: (q: RequestSeriesQuery) => api<RequestSeries>('/request-stats/series', { query: { ...q } }),
  breakdown: (q: RequestBreakdownQuery) => api<RequestBreakdown>('/request-stats/breakdown', { query: { ...q } }),
  routes: (q: RequestRoutesQuery) => api<RequestRoutesResponse>('/request-stats/routes', { query: { ...q } }),
  events: (q: RequestEventsQuery) => api<RequestEventPage>('/request-stats/events', { query: { ...q } }),
  eventsCsv: (q: RequestEventsQuery) => apiText('/request-stats/events.csv', { query: { ...q } }),
  sigmaSummary: (q: SigmaSummaryQuery) => api<SigmaSummary>('/request-stats/sigma-summary', { query: { ...q } }),
  sigmaSummaryCsv: (q: SigmaSummaryQuery) => apiText('/request-stats/sigma-summary.csv', { query: { ...q } }),
  instances: () => api<Items<ReqInstance>>('/request-stats/instances'),
  deleteInstance: (id: string) => api<void>(`/request-stats/instances/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}
